// Copyright 2026, Auxilio Technologies LLC

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersAPI, Order } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasPermission, RoleType, Permissions } from '../config/rolesConfig';
import { STATUS_BADGE, formatMoney } from '../lib/orderUtils';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

interface HistoryRow {
  id: string;
  from_status?: string;
  to_status: string;
  reason?: string;
  occurred_at: string;
}

const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const role = (user?.role || 'viewer') as RoleType;
  const canWrite = hasPermission(role, Permissions.ORDERS_WRITE);

  const [order, setOrder] = useState<Order | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!id) return;
    setLoading(true);
    ordersAPI
      .get(id)
      .then((res) => {
        setOrder(res.data.order);
        setHistory(res.data.history || []);
      })
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const advance = async (status: string) => {
    if (!id) return;
    try {
      await ordersAPI.changeStatus(id, status);
      load();
    } catch {
      load();
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>;
  if (!order) return <div className="py-12 text-center text-gray-500 dark:text-gray-400">404</div>;

  // Next status suggestions for the action bar.
  const NEXT: Record<string, string[]> = {
    new: ['in_prep', 'cancelled'],
    in_prep: ['ready', 'cancelled'],
    ready: ['out_for_delivery', 'cancelled'],
    out_for_delivery: ['delivered', 'failed'],
    failed: ['ready'],
  };

  return (
    <div className="max-w-3xl space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-rose-600 dark:text-gray-400">
        <ArrowLeftIcon className="h-4 w-4" /> {t('common.back')}
      </button>

      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {order.recipient_name}
              {order.order_number ? <span className="ml-2 text-sm font-normal text-gray-400">#{order.order_number}</span> : null}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{order.delivery_address}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_BADGE[order.status] || STATUS_BADGE.new}`}>
            {t(`order.status.${order.status}`)}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
          <Field label={t('orderBoard.slot')} value={t(`order.slot.${order.delivery_time_slot}`)} />
          <Field label={t('addOrder.deliveryDate')} value={order.delivery_date} />
          <Field label={t('orderBoard.source')} value={t(`order.source.${order.source}`)} />
          <Field label={t('orderBoard.arrangement')} value={order.arrangement_type || '—'} />
          <Field label={t('orderBoard.value')} value={formatMoney(order.order_value_cents)} />
          <Field label={t('addOrder.recipientPhone')} value={order.recipient_phone || '—'} />
          <Field label={t('addOrder.notifyMode')} value={t(`order.notify.${order.notify_mode}`)} />
          <Field label={t('addOrder.paymentStatus')} value={order.payment_status || '—'} />
        </div>

        {(order.card_message || order.special_notes) && (
          <div className="space-y-3 border-t border-gray-200 p-6 dark:border-gray-700">
            {order.card_message && (
              <div>
                <div className="text-xs font-medium uppercase text-gray-400">{t('detail.cardMessage')}</div>
                <p className="italic text-gray-700 dark:text-gray-300">“{order.card_message}”</p>
              </div>
            )}
            {order.special_notes && (
              <div>
                <div className="text-xs font-medium uppercase text-gray-400">{t('detail.notes')}</div>
                <p className="text-gray-700 dark:text-gray-300">{order.special_notes}</p>
              </div>
            )}
          </div>
        )}

        {canWrite && NEXT[order.status]?.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-gray-200 p-6 dark:border-gray-700">
            {NEXT[order.status].map((s) => (
              <button
                key={s}
                onClick={() => advance(s)}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  s === 'cancelled' || s === 'failed'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                → {t(`order.status.${s}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status history */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">{t('detail.history')}</h2>
        </div>
        {history.length === 0 ? (
          <div className="px-6 py-6 text-sm text-gray-500 dark:text-gray-400">{t('detail.noHistory')}</div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between px-6 py-3 text-sm">
                <span className="text-gray-700 dark:text-gray-300">
                  {h.from_status ? `${t(`order.status.${h.from_status}`)} → ` : ''}
                  <strong>{t(`order.status.${h.to_status}`)}</strong>
                  {h.reason ? <span className="text-gray-400"> — {h.reason}</span> : null}
                </span>
                <span className="text-gray-400">{new Date(h.occurred_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium uppercase text-gray-400">{label}</div>
    <div className="text-gray-900 dark:text-white">{value}</div>
  </div>
);

export default OrderDetail;
